import { formatString } from "@@common";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Grid2, IconButton, Stack, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import IconComponent from "../../../common/components/IconComponent";
import Panel from "../../../common/components/Panel";
import Spinner from "../../../common/components/Spinner";
import { useErrorHandler } from "../../../common/hooks";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { DataDisplayModel } from "../../Models/DataDisplayModel";
import { PropertyDisplayFormat } from "../../Models/PropertyDisplayFormat";
import { PropertyDisplayModel } from "../../Models/PropertyDisplayModel";
import IComponentProps from "../IComponentProps";
import PanelAction from "../Panel/PanelAction";
import getComponentLayout from "../Table/getComponentLayout";
import DisplayPropertyValue from "./DataDisplayPropertyValue";
import formatPropertyValue from "./formatPropertyValue";
import hasPropertyValueChanged from "./hasPropertyValueChanged";

/**
 * Converts an edit value to the proper format for sending to the server
 */
const convertValueForSave = (value: any, format?: PropertyDisplayFormat): any => {
  if (dayjs.isDayjs(value)) {
    return value.utc().toISOString();
  }

  if (format === PropertyDisplayFormat.Currency) {
    if (value === "" || value === null || value === undefined) {
      return null;
    }
    return typeof value === "number" ? value : parseFloat(value);
  }

  return value;
};

const DataDisplayComponent: React.FC<IComponentProps<DataDisplayModel>> = ({ component, context }) => {
  const errorHandler = useErrorHandler();

  const [data, dataPending, dataRefresh] = useObservedExpression<any>(component.GetData, context, "DataDisplayComponent");

  const [editingPropertyIndex, setEditingPropertyIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string | Dayjs | number | null>("");
  const [originalValue, setOriginalValue] = useState<string | Dayjs | number | null>("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Bulk edit mode state
  const [isBulkEditMode, setIsBulkEditMode] = useState<boolean>(false);
  const [bulkEditValues, setBulkEditValues] = useState<Record<string, any>>({});
  const [originalBulkValues, setOriginalBulkValues] = useState<Record<string, any>>({});

  const handleEnterEditMode = useCallback(
    (propertyIndex: number, property: PropertyDisplayModel) => {
      if (!property.UpdateData) return;

      const currentValue = formatPropertyValue(data, property);

      // Parse date/datetime values to Dayjs
      let editVal: string | Dayjs | number | null = currentValue;
      if (property.Format === PropertyDisplayFormat.Date || property.Format === PropertyDisplayFormat.DateTime) {
        const fieldValue = property.Field ? data[property.Field] : null;
        editVal = fieldValue ? dayjs(fieldValue) : null;
      } else if (property.Format === PropertyDisplayFormat.Currency) {
        // Use raw numeric value for currency editing
        const fieldValue = property.Field ? data[property.Field] : null;
        editVal = fieldValue !== null && fieldValue !== undefined ? fieldValue : "";
      }

      setEditValue(editVal);
      setOriginalValue(editVal);
      setEditingPropertyIndex(propertyIndex);
    },
    [data]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingPropertyIndex(null);
    setEditValue("");
    setOriginalValue("");
  }, []);

  const handleSaveEdit = useCallback(
    async (property: PropertyDisplayModel) => {
      if (!property.UpdateData) return;

      setIsUpdating(true);
      try {
        const eventContext = context.CreateChild();

        const valueToSend = convertValueForSave(editValue, property.Format);

        eventContext.VariableScope.set("$event", { value: valueToSend });

        await eventContext.Evaluator.evaluate(property.UpdateData, eventContext);

        // Update variables in the context variable scope
        if (property.Field) {
          context.Evaluator.assign(component.GetData, context, valueToSend, [property.Field]);
        }

        // Refresh data after update
        await dataRefresh();

        // Exit edit mode
        setEditingPropertyIndex(null);
        setEditValue("");
        setOriginalValue("");
      } catch (error) {
        errorHandler(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [editValue, context, errorHandler, dataRefresh]
  );

  const handleEnterBulkEditMode = useCallback(() => {
    if (!component.UpdateData || !data) return;

    const initialValues: Record<string, any> = {};
    const originalValues: Record<string, any> = {};

    component.Properties.forEach((prop) => {
      // Only include properties without their own UpdateData
      if (!prop.UpdateData && prop.Field) {
        const fieldValue = data[prop.Field];

        // Parse date/datetime values to Dayjs
        if (prop.Format === PropertyDisplayFormat.Date || prop.Format === PropertyDisplayFormat.DateTime) {
          initialValues[prop.Field] = fieldValue ? dayjs(fieldValue) : null;
          originalValues[prop.Field] = fieldValue ? dayjs(fieldValue) : null;
        } else {
          initialValues[prop.Field] = fieldValue !== null && fieldValue !== undefined ? fieldValue : "";
          originalValues[prop.Field] = fieldValue !== null && fieldValue !== undefined ? fieldValue : "";
        }
      }
    });

    setBulkEditValues(initialValues);
    setOriginalBulkValues(originalValues);
    setIsBulkEditMode(true);
  }, [component, data]);

  const handleCancelBulkEdit = useCallback(() => {
    setIsBulkEditMode(false);
    setBulkEditValues({});
    setOriginalBulkValues({});
  }, []);

  const handleSaveBulkEdit = useCallback(async () => {
    if (!component.UpdateData) return;

    setIsUpdating(true);
    try {
      const eventContext = context.CreateChild();
      const changedFields: Record<string, any> = {};

      // Only include fields that have changed
      Object.keys(bulkEditValues).forEach((field) => {
        const currentValue = bulkEditValues[field];
        const originalValue = originalBulkValues[field];
        const prop = component.Properties.find((p) => p.Field === field);

        const hasChanged = hasPropertyValueChanged(currentValue, originalValue, prop?.Format);

        if (hasChanged) {
          changedFields[field] = convertValueForSave(currentValue, prop?.Format);
        }
      });

      // Only proceed if there are changes
      if (Object.keys(changedFields).length > 0) {
        eventContext.VariableScope.set("$event", changedFields);
        await eventContext.Evaluator.evaluate(component.UpdateData, eventContext);

        // Update variables in the context variable scope
        for (const field in changedFields) {
          context.Evaluator.assign(component.GetData, context, changedFields[field], [field]);
        }

        // Refresh data after update
        await dataRefresh();
      }

      // Exit bulk edit mode
      setIsBulkEditMode(false);
      setBulkEditValues({});
      setOriginalBulkValues({});
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsUpdating(false);
    }
  }, [bulkEditValues, originalBulkValues, component, context, errorHandler, dataRefresh]);

  const handleBulkEditChange = useCallback((field: string, value: any) => {
    setBulkEditValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const componentLayoutSx = getComponentLayout(component);

  useEffect(() => {
    console.log(`DataDisplayComponent ${component.Id} data:`, data);
  }, [data]);

  const headerLabel = component.HeaderLabel && data ? formatString(component.HeaderLabel, data) : component.HeaderLabel;
  const headerActionSize = component.Compact ? "small" : "medium";

  // Check if there are any changed fields in bulk edit mode
  const hasBulkChanges = Object.keys(bulkEditValues).some((field) => {
    const currentValue = bulkEditValues[field];
    const originalValue = originalBulkValues[field];
    const prop = component.Properties.find((p) => p.Field === field);
    return hasPropertyValueChanged(currentValue, originalValue, prop?.Format);
  });

  // Build header actions
  const headerActions = (
    <>
      {component.UpdateData && !isBulkEditMode && (
        <IconButton size={headerActionSize} onClick={handleEnterBulkEditMode} disabled={!data || dataPending}>
          <EditIcon fontSize={headerActionSize} />
        </IconButton>
      )}
      {isBulkEditMode && (
        <>
          <IconButton size={headerActionSize} onClick={handleSaveBulkEdit} color="primary" disabled={!hasBulkChanges || isUpdating} loading={isUpdating}>
            <CheckIcon fontSize={headerActionSize} />
          </IconButton>
          <IconButton size={headerActionSize} onClick={handleCancelBulkEdit} disabled={isUpdating}>
            <CloseIcon fontSize={headerActionSize} />
          </IconButton>
        </>
      )}
      {component.HeaderActions && <PanelAction actions={component.HeaderActions} size={headerActionSize} formatData={data} context={context} />}
    </>
  );

  return (
    <Panel headerLabel={headerLabel} headerIconName={component.HeaderIconName} headerActions={headerActions} compact={component.Compact} sx={componentLayoutSx}>
      {dataPending && <Spinner />}
      {data && (
        <Grid2 container direction={component.RowDirection ? "row" : "column"} spacing={component.RowDirection ? 2 : 0.5}>
          {component.Properties.map((prop, index) => {
            var elementSx = prop.Layout ? getComponentLayout(prop) : {};
            return (
              <Grid2
                key={index}
                spacing={component.RowDirection ? 0.5 : 1}
                alignItems={component.RowDirection ? "stretch" : "center"}
                container
                size={prop.Layout?.Size}
                overflow="hidden"
                sx={{
                  ...elementSx,
                  minWidth: component.RowDirection ? 0 : undefined,
                  "& .MuiIconButton-root": {
                    visibility: editingPropertyIndex === index ? "visible" : "hidden",
                  },
                  "&:hover .MuiIconButton-root": {
                    visibility: "visible",
                  },
                }}
              >
                <Stack
                  direction={!component.RowDirection ? "row" : "column"}
                  spacing={!component.RowDirection ? 1 : 0.5}
                  alignItems={!component.RowDirection ? "center" : "flex-start"}
                  flex={prop.Flex}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: !component.RowDirection ? 120 : undefined,
                    }}
                  >
                    {prop.IconName && <IconComponent iconName={prop.IconName} size="small" />}
                    <Typography
                      variant="body2"
                      sx={{
                        color: component.RowDirection ? "text.secondary" : undefined,
                      }}
                    >
                      {prop.Label}
                    </Typography>
                  </Box>
                  <DisplayPropertyValue
                    data={data}
                    property={prop}
                    isRowDirection={component.RowDirection ?? false}
                    isEditing={isBulkEditMode && !prop.UpdateData ? true : editingPropertyIndex === index}
                    editValue={isBulkEditMode && !prop.UpdateData && prop.Field ? bulkEditValues[prop.Field] : editValue}
                    hasChanged={
                      isBulkEditMode && !prop.UpdateData && prop.Field
                        ? hasPropertyValueChanged(bulkEditValues[prop.Field], originalBulkValues[prop.Field], prop.Format)
                        : hasPropertyValueChanged(editValue, originalValue, prop.Format)
                    }
                    isUpdating={isUpdating}
                    hideSaveCancel={isBulkEditMode && !prop.UpdateData}
                    onEditChange={isBulkEditMode && !prop.UpdateData && prop.Field ? (value) => handleBulkEditChange(prop.Field!, value) : setEditValue}
                    onSave={() => handleSaveEdit(prop)}
                    onCancel={handleCancelEdit}
                    onEnterEditMode={() => handleEnterEditMode(index, prop)}
                  />
                </Stack>
              </Grid2>
            );
          })}
        </Grid2>
      )}
    </Panel>
  );
};

export default DataDisplayComponent;
